import { ChatKit, ChatKitOptions, useChatKit } from "@openai/chatkit-react";
import React, { useState } from "react";
import styles from "./Chatkit.module.scss";

interface ChatkitComponentProps {
  clientId?: number;
  modelId?: string;
  className?: string;
  style?: React.CSSProperties;
  /** If true, starts in closed state showing only the floating button */
  defaultClosed?: boolean;
  /** Called when the chat is opened */
  onOpen?: () => void;
  /** Called when the chat is closed */
  onClose?: () => void;
}

// Icon components
const ExpandIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const CollapseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="4 14 10 14 10 20" />
    <polyline points="20 10 14 10 14 4" />
    <line x1="14" y1="10" x2="21" y2="3" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.9275 12.0033L6.25 14.375L5.5725 12.0033C5.39739 11.3907 5.06908 10.8328 4.61855 10.3823C4.16802 9.93175 3.61012 9.60344 2.9975 9.42833L0.625 8.75L2.99667 8.0725C3.60928 7.89739 4.16718 7.56908 4.61772 7.11855C5.06825 6.66802 5.39656 6.11012 5.57167 5.4975L6.25 3.125L6.9275 5.49667C7.10261 6.10928 7.43092 6.66718 7.88145 7.11772C8.33198 7.56825 8.88988 7.89656 9.5025 8.07167L11.875 8.75L9.50333 9.4275C8.89072 9.60261 8.33282 9.93092 7.88228 10.3815C7.43175 10.832 7.10344 11.3899 6.92833 12.0025L6.9275 12.0033ZM13.9658 6.0125L13.75 6.875L13.5342 6.0125C13.4106 5.51799 13.155 5.06635 12.7947 4.70586C12.4343 4.34537 11.9828 4.08958 11.4883 3.96583L10.625 3.75L11.4883 3.53417C11.9828 3.41042 12.4343 3.15463 12.7947 2.79414C13.155 2.43365 13.4106 1.98201 13.5342 1.4875L13.75 0.625L13.9658 1.4875C14.0894 1.98211 14.3452 2.43382 14.7057 2.79432C15.0662 3.15482 15.5179 3.41056 16.0125 3.53417L16.875 3.75L16.0125 3.96583C15.5179 4.08944 15.0662 4.34518 14.7057 4.70568C14.3452 5.06618 14.0894 5.51789 13.9658 6.0125ZM12.8283 15.8892L12.5 16.875L12.1717 15.8892C12.0796 15.613 11.9245 15.3621 11.7187 15.1563C11.5129 14.9505 11.262 14.7954 10.9858 14.7033L10 14.375L10.9858 14.0467C11.262 13.9546 11.5129 13.7995 11.7187 13.5937C11.9245 13.3879 12.0796 13.137 12.1717 12.8608L12.5 11.875L12.8283 12.8608C12.9204 13.137 13.0755 13.3879 13.2813 13.5937C13.4871 13.7995 13.738 13.9546 14.0142 14.0467L15 14.375L14.0142 14.7033C13.738 14.7954 13.4871 14.9505 13.2813 15.1563C13.0755 15.3621 12.9204 15.613 12.8283 15.8892Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * SuperDoc Chat Component - Healthcare assistant chat interface
 *
 * Features:
 * - Built-in history panel via ChatKit (click the history icon in ChatKit header)
 * - Expand/collapse to full screen
 * - Close to floating button mode
 * - OpenAI ChatKit integration with custom backend
 */
const ChatkitComponent: React.FC<ChatkitComponentProps> = ({
  clientId,
  modelId = "gpt4",
  className,
  style,
  defaultClosed = false,
  onOpen,
  onClose,
}) => {
  const baseUrl = process.env.REACT_APP_SERVER_URL || "";

  const [isOpen, setIsOpen] = useState(!defaultClosed);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleOpen = () => {
    setIsOpen(true);
    onOpen?.();
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsExpanded(false);
    onClose?.();
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  /**
   * Custom fetch function that injects authentication and context headers.
   */
  const customFetch: typeof fetch = async (input, init) => {
    const token = localStorage.getItem("token");
    const headers = new Headers(init?.headers);

    headers.set("Content-Type", "application/json");

    if (token) {
      headers.set("Authorization", `Token ${token}`);
    }

    if (clientId) {
      headers.set("X-Client-ID", clientId.toString());
    }

    if (modelId) {
      headers.set("X-Model", modelId);
    }

    return fetch(input, {
      ...init,
      headers,
      credentials: "include",
    });
  };

  const chatkitOptions: ChatKitOptions = {
    api: {
      url: `${baseUrl}/superdoc/`,
      domainKey:
        process.env.REACT_APP_OPENAI_DOMAIN_KEY || "domain_pk_697a58d598288194860d3400dee1f1c00d82763431390419",
      fetch: customFetch,
    },
    theme: {
      colorScheme: "light",
      color: {
        accent: {
          primary: "#6366F1",
          level: 2,
        },
      },
      radius: "round",
      density: "normal",
    },
    header: {
      enabled: true,
      title: {
        enabled: true,
        text: "Nudge : Superdoc :)",
      },
    },
    composer: {
      placeholder: "Ask Superdoc anything...",
    },
    disclaimer: {
      text: "Superdoc is experimental. Verify everything.",
    },
    startScreen: {
      greeting: "Hi, I'm Superdoc! How can I help you today?",
      prompts: [
        {
          label: "Summarize today's notes",
          prompt: "Can you summarize the notes I created today?",
          icon: "document",
        },
        {
          label: "Help with diagnosis",
          prompt: "Help me think through a differential diagnosis for my current patient.",
          icon: "lightbulb",
        },
        {
          label: "Treatment suggestions",
          prompt: "What treatment options should I consider for my patient?",
          icon: "sparkle",
        },
      ],
    },
    // Enable ChatKit's built-in history panel
    history: {
      enabled: false,
    },
  };

  const { control: chatkitControl } = useChatKit(chatkitOptions);

  // If closed, show only the floating button
  if (!isOpen) {
    return (
      <button
        className={styles["superdoc__floating-button"]}
        onClick={handleOpen}
        type="button"
        aria-label="Open Superdoc"
      >
        <SparkleIcon />
        <span>Ask Superdoc</span>
      </button>
    );
  }

  return (
    <>
      {/* Overlay for expanded mode */}
      {isExpanded && (
        <div
          className={`${styles.superdoc__overlay} ${styles["superdoc__overlay--visible"]}`}
          onClick={() => setIsExpanded(false)}
        />
      )}

      <div
        className={`${styles.superdoc__wrapper} ${isExpanded ? styles["superdoc__wrapper--expanded"] : ""} ${className || ""}`}
        style={!isExpanded ? style : undefined}
      >
        {/* Control buttons overlay - positioned on top of ChatKit header */}
        <div className={styles["superdoc__controls"]}>
          <button
            className={styles["superdoc__controls-button"]}
            onClick={toggleExpand}
            type="button"
            aria-label={isExpanded ? "Collapse" : "Expand"}
            title={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
          </button>
          <button
            className={styles["superdoc__controls-button"]}
            onClick={handleClose}
            type="button"
            aria-label="Close"
            title="Close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* ChatKit Content */}
        <div className={styles.superdoc__content}>
          <ChatKit control={chatkitControl} />
        </div>
      </div>
    </>
  );
};

export default ChatkitComponent;
